import React, { useEffect, useState } from 'react';
import { Modal, Button, Spin, Alert, Tag, Typography, message } from 'antd';
import { PrinterOutlined, CopyOutlined, FormOutlined } from '@ant-design/icons';
import QRCode from 'react-qr-code';
import moment from 'moment';
import { educationSessionService } from '@iso/services';
import LiveAttendeeRoster from './LiveAttendeeRoster';

const { Paragraph } = Typography;

/**
 * The QR code for one training session, reopenable at any time.
 *
 * The wizard shows a QR once, immediately after saving — which is no help the
 * next morning when the same session runs again for the late shift, or when the
 * projector wasn't working. This modal is the durable way back to it, and it is
 * what the Records tab hangs its "Show QR" action on.
 *
 * The URL is always taken from the server (registration_url), never rebuilt
 * here, so a route change stays a single backend edit.
 */
const SessionQrModal = ({ visible, sessionId, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [session, setSession] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!visible || !sessionId) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await educationSessionService.getById(sessionId);
        if (cancelled) return;
        if (res && res.education_session) {
          setSession(res.education_session);
        } else {
          setError('This training session could not be loaded.');
        }
      } catch (e) {
        if (!cancelled) setError('This training session could not be loaded.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [visible, sessionId]);


  const url = session && session.registration_url;
  const assessment = (session && session.assessment) || null;

  const handleCopy = () => {
    if (!url) return;
    // clipboard.writeText needs a secure context; on plain http the trainer
    // still has the URL printed under the code, so a failure is a nudge rather
    // than a dead end.
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url)
        .then(() => message.success('Link copied'))
        .catch(() => message.info('Copy is unavailable here — the link is shown below the code.'));
    } else {
      message.info('Copy is unavailable here — the link is shown below the code.');
    }
  };

  // Print just the code, not the whole admin page around it. A new window with
  // its own minimal document is the only reliable way to do that across
  // browsers without fighting the app's print stylesheet.
  const handlePrint = () => {
    if (!url) return;
    const win = window.open('', '_blank', 'width=600,height=760');
    if (!win) {
      message.error('Your browser blocked the print window. Allow pop-ups and try again.');
      return;
    }
    const svg = document.getElementById('training-session-qr');
    const markup = svg ? svg.outerHTML : '';
    const title = (session && (session.training_name || (session.education_asset && session.education_asset.name))) || 'Training session';
    const when = session && session.session_date ? moment(session.session_date).format('DD/MM/YYYY') : '';

    win.document.write(`
      <html>
        <head>
          <title>${title}</title>
          <style>
            body { font-family: Arial, Helvetica, sans-serif; text-align: center; padding: 40px 24px; }
            h1 { font-size: 22px; margin: 0 0 4px; }
            p.sub { color: #666; margin: 0 0 24px; }
            .code { display: inline-block; padding: 16px; border: 1px solid #ddd; border-radius: 8px; }
            p.url { color: #888; font-size: 11px; word-break: break-all; margin-top: 16px; }
            p.hint { margin-top: 20px; font-size: 14px; }
          </style>
        </head>
        <body>
          <h1>${title}</h1>
          <p class="sub">${when}</p>
          <div class="code">${markup}</div>
          <p class="hint">Scan with the AirAgri app to register your attendance${assessment && assessment.has_assessment ? ' and complete the assessment' : ''}.</p>
          <p class="url">${url}</p>
        </body>
      </html>
    `);
    win.document.close();
    win.focus();
    win.print();
  };

  return (
    <Modal
      title="Training session QR code"
      open={visible}
      onCancel={onClose}
      width={820}
      footer={[
        <Button key="copy" icon={<CopyOutlined />} onClick={handleCopy} disabled={!url}>Copy link</Button>,
        <Button key="print" icon={<PrinterOutlined />} onClick={handlePrint} disabled={!url}>Print</Button>,
        <Button key="close" type="primary" onClick={onClose}>Close</Button>,
      ]}
      destroyOnClose
    >
      {loading && (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin />
        </div>
      )}

      {!loading && error && <Alert type="error" showIcon message={error} />}

      {!loading && !error && session && (
        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ textAlign: 'center', flex: '1 1 340px', minWidth: 300 }}>
          <div style={{ fontWeight: 600, fontSize: 16 }}>
            {session.training_name || (session.education_asset && session.education_asset.name) || 'Training session'}
          </div>
          <div style={{ color: '#8c8c8c', marginBottom: 16 }}>
            {session.session_date ? moment(session.session_date).format('DD/MM/YYYY') : ''}
            {session.location_name ? ` · ${session.location_name}` : ''}
          </div>

          {/* A code that cannot register anybody is worse than no code: the
              trainer projects it, people scan it, and nothing happens. Say so
              on the same screen as the code itself. */}
          {session.is_open_for_registration === false && (
            <Alert
              style={{ marginBottom: 16, textAlign: 'left', borderRadius: 8 }}
              type="warning"
              showIcon
              message={session.status === 'draft'
                ? 'This session is still a draft'
                : 'This session is completed'}
              description={session.status === 'draft'
                ? 'Scanning will not register anyone until you activate the session.'
                : 'The attendee list is closed. Reopen the session to accept new registrations.'}
            />
          )}

          {url ? (
            <>
              <div style={{ display: 'inline-block', padding: 16, background: '#fff', border: '1px solid #e8e8e8', borderRadius: 8 }}>
                <QRCode id="training-session-qr" value={url} size={200} />
              </div>

              {assessment && assessment.has_assessment && (
                <Alert
                  style={{ marginTop: 16, textAlign: 'left', borderRadius: 8 }}
                  type="info"
                  showIcon
                  icon={<FormOutlined />}
                  message={<span>Scanning also opens <strong>{assessment.form_name}</strong></span>}
                  description={
                    <span>
                      {assessment.scored
                        ? `Auto-marked, pass mark ${assessment.pass_mark}%. `
                        : 'Not auto-marked — you confirm competency. '}
                      {assessment.source === 'asset'
                        ? 'Inherited from the training asset.'
                        : 'Set for this session.'}
                      {' '}
                      <Tag style={{ marginLeft: 4 }}>{assessment.submitted_count} submitted</Tag>
                    </span>
                  }
                />
              )}

              <Paragraph style={{ marginTop: 12, fontSize: 12, color: '#8c8c8c', wordBreak: 'break-all' }}>
                {url}
              </Paragraph>
            </>
          ) : (
            <Alert
              type="warning"
              showIcon
              message="No registration link"
              description="The server did not return a registration URL for this session."
            />
          )}
        </div>

        {/* Live roster. Sits beside the code so a trainer projecting this
            screen can see registrations land without touching anything. */}
        <div style={{ flex: '1 1 320px', minWidth: 280 }}>
          <LiveAttendeeRoster
            sessionId={sessionId}
            enabled={visible && !!sessionId}
          />
        </div>
        </div>
      )}
    </Modal>
  );
};

export default SessionQrModal;
